import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchItemById, updateItem } from '../services/api.ts';
import { ArrowLeft, Upload, Image as ImageIcon, Loader, ExternalLink, DollarSign } from 'lucide-react';
import './EditItem.css';

const EditItem: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    avater: '',
    price: 0,
    regular_price: 0,
    url: '',
    enable: true
  });

  useEffect(() => {
    const loadItem = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await fetchItemById(id);
        setFormData({
          name: data.name || '',
          avater: data.avater || '',
          price: data.price || 0,
          regular_price: data.regular_price || 0,
          url: data.url || '',
          enable: data.enable || true
        });
        setImagePreview(data.avater || '');
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load item');
        console.error('Error loading item:', err);
      } finally {
        setLoading(false);
      }
    };

    loadItem();
  }, [id]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setFormData({ ...formData, avater: url });
    setImagePreview(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setError(null);
      await updateItem(id, formData);
      navigate('/dashboard/items');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to update item';
      setError(errorMessage);
      console.error('Update error:', err.response?.data);
    }
  };

  const calculateDiscount = () => {
    if (formData.regular_price <= 0 || formData.price >= formData.regular_price) return 0;
    return Math.round(((formData.regular_price - formData.price) / formData.regular_price) * 100);
  };

  if (loading) {
    return (
      <div className="edit-item">
        <div className="loading-container">
          <Loader className="loading-spinner-icon" size={40} />
          <p>Loading item...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-item">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/items')}>
          <ArrowLeft size={20} />
          Back to Items
        </button>
        <h2>Edit Item</h2>
      </div>

      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>
            
            <div className="form-group">
              <label>Item Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter item name"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Current Price *</label>
                <div className="input-with-icon">
                  <DollarSign size={18} className="input-icon" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Regular Price</label>
                <div className="input-with-icon">
                  <DollarSign size={18} className="input-icon" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.regular_price}
                    onChange={(e) => setFormData({ ...formData, regular_price: parseFloat(e.target.value) })}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Image URL *</label>
              <div className="input-with-icon">
                <Upload size={18} className="input-icon" />
                <input
                  type="text"
                  value={formData.avater}
                  onChange={handleImageChange}
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>
              <p className="field-hint">Enter the URL of the item image</p>
            </div>

            <div className="form-group">
              <label>Product URL</label>
              <div className="input-with-icon">
                <ExternalLink size={18} className="input-icon" />
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://example.com/product"
                />
              </div>
              <p className="field-hint">External link to the product page</p>
            </div>

            <div className="form-switches">
              <div className="switch-group">
                <div className="switch-content">
                  <label className="switch-label">Active Status</label>
                  <p className="switch-description">Enable this item for public viewing</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.enable}
                    onChange={(e) => setFormData({ ...formData, enable: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Preview</h3>
            
            <div className="preview-card">
              {imagePreview ? (
                <div className="preview-image-wrapper">
                  <img src={imagePreview} alt="Preview" className="preview-image" />
                  {calculateDiscount() > 0 && (
                    <span className="preview-discount">-{calculateDiscount()}%</span>
                  )}
                </div>
              ) : (
                <div className="preview-placeholder">
                  <ImageIcon size={48} />
                  <p>Image preview will appear here</p>
                </div>
              )}
              
              <div className="preview-details">
                <h4>{formData.name || 'Item Name'}</h4>
                
                <div className="preview-pricing">
                  <div className="preview-price-row">
                    <span className="preview-current-price">${formData.price.toFixed(2)}</span>
                    {formData.regular_price > formData.price && (
                      <span className="preview-regular-price">${formData.regular_price.toFixed(2)}</span>
                    )}
                  </div>
                  {formData.url && (
                    <a href={formData.url} target="_blank" rel="noopener noreferrer" className="preview-link">
                      <ExternalLink size={14} />
                      View Product
                    </a>
                  )}
                </div>

                <div className="preview-badges">
                  <span className={`preview-badge ${formData.enable ? 'active' : 'inactive'}`}>
                    {formData.enable ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-box">
              <h4>💡 Tips</h4>
              <ul>
                <li>Use high-quality product images</li>
                <li>Set regular price higher than current price for discount display</li>
                <li>Include product URL for customer reference</li>
                <li>Keep item names clear and descriptive</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={() => navigate('/dashboard/items')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditItem;
